/**
 * Wo der **Ausschnitt** auf der **Karte** liegt — in Pixeln der Karte.
 *
 * **Warum das hier nachgerechnet wird.** React Flow zeichnet den Ausschnitt
 * selbst, aber als Teil seiner Maske: EIN Pfad aus zwei Rechtecken, ohne `rx`.
 * Ein Rundungsradius ist daran nicht zu haben. Die Karte zeichnet den Rahmen
 * deshalb als eigenes Element darüber und braucht dafür seine Lage.
 *
 * **Die Rechnung ist die der Bibliothek**, Zeile für Zeile aus
 * `MiniMapComponent` (`@xyflow/react`, `dist/esm/index.js`):
 *
 * ```
 * viewScale  = max(bounds.width / elWidth, bounds.height / elHeight)
 * viewWidth  = viewScale * elWidth
 * viewHeight = viewScale * elHeight
 * offset     = offsetScale * viewScale
 * x          = bounds.x - (viewWidth - bounds.width) / 2 - offset
 * y          = bounds.y - (viewHeight - bounds.height) / 2 - offset
 * ```
 *
 * Läuft sie auseinander, sitzt der Rahmen neben dem, was die Maske zeigt —
 * deshalb steht sie hier als reine Funktion und nicht verstreut im Bauteil.
 *
 * Bewusst ohne React- und DOM-Bezug, wie die übrigen `utils/`.
 */

/** Ein Rechteck, wie React Flow es führt. */
export interface MapRect {
  x: number
  y: number
  width: number
  height: number
}

/** Die Lage des Rahmens, fertig für `style`. */
export interface MapViewportBox {
  left: number
  top: number
  width: number
  height: number
}

/**
 * React Flows Vorgabe für den Rand um den Inhalt (`offsetScale`). Sie ist
 * dimensionslos und wird mit dem Maßstab multipliziert.
 */
export const MAP_OFFSET_SCALE = 5

/**
 * @param bounds   die Ausdehnung, die die Karte zeigt — Elemente UND Ausschnitt
 * @param viewport der Ausschnitt in Canvas-Koordinaten
 * @param size     die Maße der Karte in Pixeln
 */
export function canvasMapViewportBox(
  bounds: MapRect,
  viewport: MapRect,
  size: { width: number; height: number },
): MapViewportBox | null {
  if (size.width <= 0 || size.height <= 0) return null
  if (bounds.width <= 0 || bounds.height <= 0) return null

  const viewScale = Math.max(bounds.width / size.width, bounds.height / size.height)
  if (!Number.isFinite(viewScale) || viewScale <= 0) return null

  const viewWidth = viewScale * size.width
  const viewHeight = viewScale * size.height
  const offset = MAP_OFFSET_SCALE * viewScale

  // Die viewBox: linke obere Ecke in Canvas-Koordinaten, plus der Rand ringsum.
  const boxX = bounds.x - (viewWidth - bounds.width) / 2 - offset
  const boxY = bounds.y - (viewHeight - bounds.height) / 2 - offset
  const boxWidth = viewWidth + offset * 2
  const boxHeight = viewHeight + offset * 2

  /*
    **Der Rand kippt das Seitenverhältnis.** `viewWidth`/`viewHeight` haben
    exakt das der Karte, aber `offset` kommt auf BEIDEN Achsen gleich dazu —
    danach stimmt es nicht mehr. Das SVG passt seine viewBox deshalb ein
    (`preserveAspectRatio` steht auf der Vorgabe `xMidYMid meet`): Es nimmt den
    kleineren der beiden Maßstäbe und zentriert, was übrig bleibt.

    Ohne diese beiden Zeilen säße der Rahmen um ein paar Pixel neben dem, was
    die Karte zeichnet — wenig, aber bei 168px Breite sichtbar.
  */
  const scale = Math.min(size.width / boxWidth, size.height / boxHeight)
  const padX = (size.width - boxWidth * scale) / 2
  const padY = (size.height - boxHeight * scale) / 2

  return {
    left: (viewport.x - boxX) * scale + padX,
    top: (viewport.y - boxY) * scale + padY,
    width: viewport.width * scale,
    height: viewport.height * scale,
  }
}

/**
 * Die Ausdehnung, die die Karte zeigt: alle Elemente UND der Ausschnitt.
 *
 * Der Ausschnitt gehört dazu, weil die Karte gerade dann kommt, wenn er
 * außerhalb der Elemente liegt — ohne ihn ließe sich „wo bin ich" nicht
 * beantworten (CONTEXT.md, `**Karte**`). Ohne Elemente ist er die ganze
 * Ausdehnung; dieselbe Regel wie in der Bibliothek.
 */
export function unionRect(a: MapRect | null, b: MapRect): MapRect {
  if (!a) return b
  const left = Math.min(a.x, b.x)
  const top = Math.min(a.y, b.y)
  const right = Math.max(a.x + a.width, b.x + b.width)
  const bottom = Math.max(a.y + a.height, b.y + b.height)
  return { x: left, y: top, width: right - left, height: bottom - top }
}
